import { useRecoilValue } from 'recoil';
import { useHippoText } from '../../hooks/useHippoText';
import CustomerBenefitText from '../CustomerBenefitText/CustomerBenefitText';
import { timeLimitedDiscountsAtom } from '../../recoil/atoms/timeLimitedDiscountsAtom';
import type { TimeLimitedDiscountDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/common';
import { getCoverableByIdAndFlavourCode } from '../../utils/coverablesUtil';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import { convertLocalDateDTOToDate, toLongDateString } from '../../utils/dateUtil';
import { getCurrencyString } from '../../utils/currencyUtil';

enum DiscountTypes {
    CarDiscount = 'Car discount',
    Salesboost = 'Salesboost',
    Error = 'Error',
    RoadAssistance = 'Road Assistance Time Guarantee',
    Combination = 'Combination'
}

const TimeLimitedDiscounts = ({
    interactive = true,
    shouldRenderShoppingBasketDesign = false
}: {
    interactive?: boolean;
    shouldRenderShoppingBasketDesign?: boolean;
}): React.JSX.Element => {
    const discounts = useRecoilValue(timeLimitedDiscountsAtom);
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const getHippoText = useHippoText();

    const discountByCoverable = discounts.reduce<Record<string, TimeLimitedDiscountDTO>>(
        (previous, discount) => {
            const { coverableFixedID, flavourCode, discountType } = discount;
            const key = flavourCode + coverableFixedID;
            if (discountType === DiscountTypes.RoadAssistance) {
                previous[`${key}-road`] = discount;
            } else if (key in previous) {
                if (previous[key].discountType !== discountType) {
                    previous[key] = {
                        ...previous[key],
                        discountType: DiscountTypes.Combination
                    };
                }
                const prevDate = convertLocalDateDTOToDate(previous[key].expirationDate);
                const discountDate = convertLocalDateDTOToDate(discount.expirationDate);
                if (prevDate < discountDate) {
                    previous[key] = {
                        ...previous[key],
                        expirationDate: discount.expirationDate
                    };
                }
            } else {
                previous[key] = discount;
            }
            return previous;
        },
        {}
    );

    const discountTexts = Object.values(discountByCoverable).map((discount) => {
        const { coverableFixedID, flavourCode, coverableOriginalPrice, discountType } = discount;
        const { amount: originalPrice = 0 } = coverableOriginalPrice;
        const coverable = getCoverableByIdAndFlavourCode(
            sortedCoverables,
            coverableFixedID,
            flavourCode
        );
        if (!coverable) {
            return '';
        }
        const { flavourName = '', name = '', yearlyPrice } = coverable;
        const adjustedPrice = yearlyPrice?.amount ?? 0;
        const textVariables = {
            flavourName: flavourName.toLowerCase(),
            objectIdentifier: name,
            expirationDate: toLongDateString(discount.expirationDate),
            originalPrice: getCurrencyString(originalPrice, ''),
            adjustedPrice: getCurrencyString(adjustedPrice, ''),
            discountEffectiveDateYear: discount.discountEffectiveDate.year ?? 0
        };
        switch (discountType) {
            case DiscountTypes.RoadAssistance:
                return getHippoText('DSS-TLD-MODAL-ROAD-ASSISTANCE', textVariables);
            case DiscountTypes.CarDiscount:
                return getHippoText('DSS-TLD-MODAL-CAR-DISCOUNT', textVariables);
            case DiscountTypes.Error:
                return getHippoText('DSS-TLD-MODAL-ERROR', textVariables);
            case DiscountTypes.Salesboost:
                return getHippoText('DSS-TLD-MODAL-SALES-BOOST', textVariables);
            case DiscountTypes.Combination:
                return getHippoText('DSS-TLD-MODAL-COMBINATION', textVariables);
            default:
                return 'Invalid discount type';
        }
    });

    if (discounts.length === 0) {
        return <></>;
    }

    const discountHtml =
        discountTexts.reduce((currentHtml, discountText) => {
            return `${currentHtml}<div class="u-spacing-stack-xl">${discountText}</div><hr />`;
        }, '') + getHippoText('DSS-TLD-MODAL-GENERAL');

    return (
        <CustomerBenefitText
            key={'benefitType'}
            interactive={interactive}
            title={getHippoText('DSS-TLD-TITLE')}
            rawHtmlDescription={discountHtml}
            shouldRenderShoppingBasketDesign={shouldRenderShoppingBasketDesign}
        />
    );
};

export default TimeLimitedDiscounts;
